import { useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import { socials } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { inspoColumns, inspoPhotos, instagramUrl, type InspoPhoto } from "@/lib/setup-inspiration";
import { useAutoplay } from "@/lib/use-autoplay";
import { SectionHeading } from "./SectionHeading";

const ARROW = "size-10 bg-white shadow-md";

/**
 * The creator photo gallery (DEL-521). Divine retitled it "Best Standing
 * Desks in Indonesia" on 3 Nov, for search, with a trust line under it.
 *
 * Creator photos two rows deep and five columns across on a wide screen
 * (two on a phone), as wide as the page allows. The strip scrolls a column
 * at a time with the arrows or a swipe, and loops, so there is always more
 * to see. It also moves on its own, slowly (the arrows are there for anyone
 * in a hurry), pausing under the pointer. A photo opens full size in the
 * lightbox.
 */
export function SetupInspiration() {
  const [open, setOpen] = useState<number | null>(null);
  const { plugins, setApi } = useAutoplay(4500);

  return (
    <section id="inspirasi" className="border-t border-ink/8 py-14 sm:py-20">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6">
        {/* One line under the title, not two (3 Nov): the heading is as wide as
            the section, and the line only wraps when the screen is too narrow. */}
        <SectionHeading
          title="Best Standing Desks in Indonesia"
          lead={
            <span className="block text-xl font-medium text-ink/80 sm:text-2xl">
              Dipercaya oleh lebih dari 5.000 pengguna di berbagai rumah, kantor, dan organisasi terkemuka.
            </span>
          }
          align="center"
          className="max-w-none"
        />

        <Carousel
          setApi={setApi}
          opts={{ align: "start", loop: true }}
          plugins={plugins}
          className="mt-10"
          aria-label="Inspirasi workspace setup"
        >
          <CarouselContent className="-ml-2 sm:-ml-3">
            {inspoColumns.map((column, i) => (
              <CarouselItem key={i} className="basis-1/2 pl-2 sm:basis-1/3 sm:pl-3 lg:basis-1/5">
                <div className="grid gap-2 sm:gap-3">
                  {column.map((photo) => (
                    <InspoTile key={photo.thumb} photo={photo} onOpen={() => setOpen(inspoPhotos.indexOf(photo))} />
                  ))}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className={`left-2 sm:-left-1 ${ARROW}`} />
          <CarouselNext className={`right-2 sm:-right-1 ${ARROW}`} />
        </Carousel>

        <div className="mt-10 text-center">
          <Button asChild variant="outline">
            <a href={socials.instagram} target="_blank" rel="noopener noreferrer">
              Lihat lebih banyak di Instagram
              <ArrowUpRight />
            </a>
          </Button>
        </div>
      </div>

      <InspoLightbox photos={inspoPhotos} index={open} onIndexChange={setOpen} />
    </section>
  );
}

function InspoTile({ photo, onOpen }: { photo: InspoPhoto; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative block aspect-square w-full overflow-hidden rounded-xl bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
    >
      <img
        src={photo.thumb}
        alt={`Setup meja kerja ${photo.creator.name}`}
        loading="lazy"
        width={720}
        height={720}
        className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
    </button>
  );
}

function InspoLightbox({
  photos,
  index,
  onIndexChange,
}: {
  photos: InspoPhoto[];
  index: number | null;
  onIndexChange: (index: number | null) => void;
}) {
  const photo = index === null ? undefined : photos[index];
  const step = (by: number) =>
    index !== null && onIndexChange((index + by + photos.length) % photos.length);

  return (
    <Dialog open={photo !== undefined} onOpenChange={(o) => !o && onIndexChange(null)}>
      <DialogContent
        className="w-[min(92vw,1100px)]"
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") step(-1);
          if (e.key === "ArrowRight") step(1);
        }}
      >
        {photo && (
          <figure>
            <img
              key={photo.full}
              src={photo.full}
              alt={`Setup meja kerja ${photo.creator.name}`}
              className="mx-auto max-h-[80vh] w-auto rounded-2xl object-contain"
            />
            <figcaption className="mt-3 flex items-center justify-between gap-3 text-white">
              <div>
                <DialogTitle className="text-sm font-medium">Setup {photo.creator.name}</DialogTitle>
                <DialogDescription asChild>
                  {photo.creator.handle ? (
                    <a
                      href={instagramUrl(photo.creator.handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
                    >
                      <Instagram className="size-3" />@{photo.creator.handle}
                    </a>
                  ) : (
                    <span className="text-xs text-white/70">Pakai meja Kanata</span>
                  )}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="white" onClick={() => step(-1)} aria-label="Foto sebelumnya">
                  <ArrowLeft />
                </Button>
                <Button size="icon" variant="white" onClick={() => step(1)} aria-label="Foto berikutnya">
                  <ArrowRight />
                </Button>
              </div>
            </figcaption>
          </figure>
        )}
      </DialogContent>
    </Dialog>
  );
}
